import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAPABILITY_LABELS,
  createUserSchema,
  configurableCapabilities,
  WHOLESALE_ROLES,
  type Capability,
  ROLE_LABELS,
  ROLES,
  type CreateUserInput,
  type CreateUserResponse,
  type Role,
  type UserRowDTO,
} from '@bifeai/shared';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  useToast,
} from '@bifeai/ui';
import { Copy, Ellipsis, KeyRound, LogOut, Pencil, UserPlus, Users } from 'lucide-react';
import { useState, type ChangeEvent, type SelectHTMLAttributes } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { api, applyFieldErrors, errorMessage } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useSession } from '../lib/session';

const ROLE_HELP: Record<Role, string> = {
  OWNER: 'Ve todo, en todas las sucursales.',
  MANAGER: 'Maneja la sucursal: compras, mermas, desposte y anulaciones.',
  CASHIER: 'Vende y maneja su caja. No ve costos ni ganancias.',
  FRANCHISEE: 'Dueño de una franquicia: maneja su local, su equipo, su Mercado Pago y su facturación. No ve la marca ni otras franquicias.',
  WS_SELLER: 'Mayorista: ve sus clientes, toma pedidos, cobra y ve sus comisiones. No ve el mostrador ni los costos.',
  DRIVER: 'Mayorista: solo su reparto desde el celular (hoja de ruta, entregas, cobros y la vuelta de la camioneta).',
  ASSISTANT: 'Arranca sin nada: le habilitás solo lo que necesita (por ejemplo producción, desposte, compras o el mayorista).',
};

/** Roles que se crean eligiendo sus permisos en el momento. */
const PICK_ON_CREATE: readonly Role[] = ['WS_SELLER', 'DRIVER', 'ASSISTANT'];

/** Interruptores de los permisos configurables de un rol. */
function CapabilitySwitches({ caps, value, onChange }: { caps: readonly Capability[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {caps.map((cap) => (
        <label key={cap} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>{CAPABILITY_LABELS[cap]}</span>
          <Switch
            label={CAPABILITY_LABELS[cap]}
            checked={value.includes(cap)}
            onCheckedChange={(on) => onChange(on ? [...value, cap] : value.filter((c) => c !== cap))}
          />
        </label>
      ))}
    </div>
  );
}

/** Roles que puede asignar quien está usando BifeAI. El franquiciado lo da de alta solo la casa central. */
function useAssignableRoles(): Role[] {
  const { me, isHeadquartersUser, franchisesEnabled } = useSession();
  const hasFranchises = me.branches.some((b) => b.franchise);
  return ROLES.filter((r) => {
    if (r === 'OWNER') return me.membership.role === 'OWNER';
    if (r === 'FRANCHISEE') return franchisesEnabled && isHeadquartersUser && hasFranchises;
    // Vendedor mayorista y repartidor: con el módulo Mayorista y desde la marca.
    if (WHOLESALE_ROLES.includes(r)) return me.tenant.modules.includes('WHOLESALE') && isHeadquartersUser;
    return true;
  });
}

export function UsersPage() {
  const { me, isHeadquartersUser } = useSession();
  const franchiseIds = new Set(me.branches.filter((b) => b.franchise).map((b) => b.id));
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserRowDTO | null>(null);
  const [secret, setSecret] = useState<{
    title: string;
    email: string;
    password: string;
  } | null>(null);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRowDTO[]>('/users'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });

  const reset = useMutation({
    mutationFn: (u: UserRowDTO) => api.post<{ temporaryPassword: string }>(`/users/${u.membershipId}/reset-password`),
    onSuccess: (res, u) =>
      setSecret({
        title: 'Contraseña temporal nueva',
        email: u.email,
        password: res.temporaryPassword,
      }),
    onError: (err) => toast.error('No se pudo generar la contraseña', errorMessage(err)),
  });
  const revoke = useMutation({
    mutationFn: (u: UserRowDTO) => api.post<{ revoked: number }>(`/users/${u.membershipId}/revoke-sessions`),
    onSuccess: (res, u) =>
      toast.success('Sesiones cerradas', res.revoked ? `${u.name} tiene que volver a ingresar.` : `${u.name} no tenía sesiones abiertas.`),
    onError: (err) => toast.error('No se pudieron cerrar las sesiones', errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Usuarios"
        subtitle="Cada persona entra con su usuario. Así queda registrado quién vendió, anuló o cambió algo."
        actions={
          <Button variant="primary" icon={<UserPlus />} onClick={() => setCreating(true)}>
            Nuevo usuario
          </Button>
        }
      />

      <Card>
        <CardBody flush>
          {users.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : users.data?.length === 0 ? (
            <EmptyState icon={<Users />} title="Sin usuarios" text="Sumá a tu equipo para que cada uno entre con su usuario." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Rol</th>
                    <th>Sucursal</th>
                    <th>Seguridad</th>
                    <th>Último ingreso</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {users.data?.map((u) => {
                    const self = u.userId === me.user.id;
                    return (
                      <tr key={u.membershipId}>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              gap: 12,
                              alignItems: 'center',
                            }}
                          >
                            <Avatar name={u.name} />
                            <div>
                              <div className="bf-cell-main">
                                {u.name} {self && <span className="bf-cell-sub">(vos)</span>}
                              </div>
                              <div className="bf-cell-sub">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge tone={u.role === 'OWNER' ? 'info' : 'neutral'}>{ROLE_LABELS[u.role]}</Badge>
                        </td>
                        <td>{u.branchName ?? <span className="bf-cell-sub">Todas</span>}</td>
                        <td>
                          {!u.isActive ? (
                            <Badge tone="danger" dot>
                              Desactivado
                            </Badge>
                          ) : u.totpEnabled ? (
                            <Badge tone="success" dot>
                              Dos pasos activa
                            </Badge>
                          ) : (
                            <Badge dot>Solo contraseña</Badge>
                          )}
                        </td>
                        <td className="bf-cell-sub">{u.lastLoginAt ? timeAgo(u.lastLoginAt) : 'Nunca ingresó'}</td>
                        <td className="bf-cell-actions">
                          {!self && isHeadquartersUser && u.role !== 'FRANCHISEE' && franchiseIds.has(u.branchId ?? '') ? (
                            <span className="bf-cell-sub">Lo maneja el franquiciado</span>
                          ) : (
                            !self && (
                              <Menu>
                                <MenuTrigger asChild>
                                  <button className="bf-icon-btn" aria-label={`Acciones para ${u.name}`}>
                                    <Ellipsis />
                                  </button>
                                </MenuTrigger>
                                <MenuContent>
                                  <MenuItem icon={<Pencil />} onSelect={() => setEditing(u)}>
                                    Editar rol y sucursal
                                  </MenuItem>
                                  <MenuItem icon={<KeyRound />} onSelect={() => reset.mutate(u)}>
                                    Generar contraseña temporal
                                  </MenuItem>
                                  <MenuSeparator />
                                  <MenuItem icon={<LogOut />} danger onSelect={() => revoke.mutate(u)}>
                                    Cerrar todas sus sesiones
                                  </MenuItem>
                                </MenuContent>
                              </Menu>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {creating && (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={async (res) => {
            setCreating(false);
            setSecret({
              title: `${res.user.name} ya tiene usuario`,
              email: res.user.email,
              password: res.temporaryPassword,
            });
            await refresh();
          }}
        />
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            toast.success('Cambios guardados', `${editing.name} tiene que volver a ingresar para ver los cambios.`);
            setEditing(null);
            await refresh();
          }}
        />
      )}

      {secret && <TemporaryPasswordDialog {...secret} onClose={() => setSecret(null)} />}
    </>
  );
}

function BranchSelect(props: SelectHTMLAttributes<HTMLSelectElement> & { allowAll: boolean; onlyFranchises?: boolean }) {
  const { me, isHeadquartersUser } = useSession();
  const { allowAll, onlyFranchises, ...rest } = props;
  // La central da de alta en una franquicia solo al Franquiciado; su equipo lo arma él.
  const skipFranchises = isHeadquartersUser && !onlyFranchises;
  return (
    <Select {...rest}>
      {allowAll && isHeadquartersUser && <option value="">Todas las sucursales</option>}
      {me.branches
        .filter((b) => (onlyFranchises ? b.franchise : !skipFranchises || !b.franchise))
        .map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
    </Select>
  );
}

function CreateUserDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (r: CreateUserResponse) => Promise<void> }) {
  const { me } = useSession();
  const [formError, setFormError] = useState<string | null>(null);
  const defaultBranch = me.membership.branchId ?? me.branches[0]?.id ?? null;
  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'CASHIER',
      branchId: defaultBranch,
      capabilities: [],
    },
  });
  const role = watch('role');
  const roles = useAssignableRoles();
  const pickCaps = PICK_ON_CREATE.includes(role) ? configurableCapabilities(role, { wholesale: me.tenant.modules.includes('WHOLESALE') }) : [];

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const { capabilities, ...rest } = values;
      const res = await api.post<CreateUserResponse>('/users', pickCaps.length ? { ...rest, capabilities } : rest);
      await onCreated(res);
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nuevo usuario"
      description="Le generamos una contraseña temporal que va a cambiar en su primer ingreso."
    >
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Nombre y apellido" error={errors.name?.message}>
          <Input autoFocus {...register('name')} />
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" placeholder="nombre@gmail.com" {...register('email')} />
        </Field>
        <Field label="Rol" hint={ROLE_HELP[role]} error={errors.role?.message}>
          <Select
            {...register('role', {
              onChange: (e: ChangeEvent<HTMLSelectElement>) => {
                setValue('capabilities', []);
                if (e.target.value === 'OWNER') setValue('branchId', null);
                else if (e.target.value === 'FRANCHISEE') setValue('branchId', me.branches.find((b) => b.franchise)?.id ?? null);
                else if (!watch('branchId')) setValue('branchId', defaultBranch);
              },
            })}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        {role !== 'OWNER' && (
          <Field
            label="Sucursal"
            hint={role === 'MANAGER' ? 'Un encargado de "Todas las sucursales" ve la cadena completa.' : undefined}
            error={errors.branchId?.message}
          >
            <BranchSelect
              allowAll={role === 'MANAGER' || PICK_ON_CREATE.includes(role)}
              onlyFranchises={role === 'FRANCHISEE'}
              {...register('branchId', {
                setValueAs: (v: string) => (v ? v : null),
              })}
            />
          </Field>
        )}
        {pickCaps.length > 0 && (
          <div className="bf-field">
            <span className="bf-label">{role === 'ASSISTANT' ? 'Qué puede hacer' : 'También puede'}</span>
            <Controller
              control={control}
              name="capabilities"
              render={({ field }) => <CapabilitySwitches caps={pickCaps} value={field.value ?? []} onChange={field.onChange} />}
            />
          </div>
        )}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            Crear usuario
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface EditValues {
  role: Role;
  branchId: string | null;
  isActive: boolean;
  capabilities: string[];
}

function EditUserDialog({ user, onClose, onSaved }: { user: UserRowDTO; onClose: () => void; onSaved: () => Promise<void> }) {
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, watch, control, formState } = useForm<EditValues>({
    defaultValues: {
      role: user.role,
      branchId: user.branchId,
      isActive: user.isActive,
      capabilities: user.capabilities,
    },
  });
  const role = watch('role');
  const roles = useAssignableRoles();
  const { me } = useSession();
  // Permisos que se pueden prender o apagar en su rol (al cambiar de rol, primero se guarda el rol).
  const editable = role === user.role ? configurableCapabilities(role, { wholesale: me.tenant.modules.includes('WHOLESALE') }) : [];

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.patch(`/users/${user.membershipId}`, {
        role: values.role,
        branchId: values.role === 'OWNER' ? null : values.branchId,
        isActive: values.isActive,
        ...(editable.length && values.role === user.role ? { capabilities: values.capabilities } : {}),
      });
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Editar a ${user.name}`} description={user.email}>
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Rol" hint={ROLE_HELP[role]}>
          <Select {...register('role')}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        {role !== 'OWNER' && (
          <Field label="Sucursal">
            <BranchSelect
              allowAll={role === 'MANAGER' || PICK_ON_CREATE.includes(role)}
              onlyFranchises={role === 'FRANCHISEE'}
              {...register('branchId', {
                setValueAs: (v: string) => (v ? v : null),
              })}
            />
          </Field>
        )}
        {role !== user.role && PICK_ON_CREATE.includes(role) && (
          <Callout>Guardá el cambio de rol y después volvé a abrirlo para elegir qué puede hacer.</Callout>
        )}
        {editable.length > 0 && (
          <div className="bf-field">
            <span className="bf-label">
              {role === 'MANAGER' ? 'Permisos adicionales del encargado' : role === 'ASSISTANT' ? 'Qué puede hacer' : 'Permisos adicionales'}
            </span>
            <Controller
              control={control}
              name="capabilities"
              render={({ field }) => <CapabilitySwitches caps={editable} value={field.value} onChange={field.onChange} />}
            />
          </div>
        )}
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span>
                <span className="bf-label">Usuario activo</span>
                <span className="bf-hint" style={{ display: 'block' }}>
                  Si lo desactivás, se cierran sus sesiones y no puede volver a entrar.
                </span>
              </span>
              <Switch label="Usuario activo" checked={field.value} onCheckedChange={field.onChange} />
            </label>
          )}
        />
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={formState.isSubmitting}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Se muestra una sola vez: después no se puede volver a ver. */
function TemporaryPasswordDialog({ title, email, password, onClose }: { title: string; email: string; password: string; onClose: () => void }) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Usuario: ${email}\nContraseña temporal: ${password}`);
      toast.success('Copiado', 'Pegalo en WhatsApp o en un mail para pasárselo.');
    } catch {
      toast.error('No se pudo copiar', 'Copialo a mano antes de cerrar.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={title} description="Pasale estos datos. La contraseña se muestra una sola vez.">
      <div className="bf-form">
        <div className="bf-field">
          <span className="bf-label">Usuario</span>
          <p className="bf-secret" style={{ letterSpacing: 0 }}>
            {email}
          </p>
        </div>
        <div className="bf-field">
          <span className="bf-label">Contraseña temporal</span>
          <p className="bf-secret" style={{ fontSize: 'var(--bf-text-xl)' }}>
            {password}
          </p>
        </div>
        <Callout>En su primer ingreso va a tener que elegir una contraseña propia.</Callout>
        <div className="bf-form-actions">
          <Button icon={<Copy />} onClick={() => void copy()}>
            Copiar datos
          </Button>
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
